import { useState, useEffect, useCallback } from 'react';
import { type ArticuloMateriaPrima, type DERCInfo } from '../types';
import { type DERCResponse } from '../../derc/types/derc';
import { ArticuloService } from '../../../catalogo/articulos/services/articuloService';

interface FormData {
  selectedDercId: number | null;
  selectedDercNumber: string;
  selectedProveedorId: number | null;
  selectedProveedorName: string;
  esServicio: boolean;
  observaciones: string;
  notas: string;
  dercInfo: DERCInfo | null;
}

interface FormErrors {
  selectedDercId?: string;
  selectedProveedorId?: string;
  productos?: string;
}

interface UseGuiaMateriaFormProps {
  onSubmit: (data: any) => void;
  isOpen: boolean;
  productType?: 'A' | 'B' | null;
  initialData?: {
    id: number;
    formData: FormData;
    productos: ArticuloMateriaPrima[];
  } | null;
}

export const useGuiaMateriaForm = ({ onSubmit, isOpen, productType, initialData }: UseGuiaMateriaFormProps) => {
  const [guiaId, setGuiaId] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({
    selectedDercId: null,
    selectedDercNumber: '',
    selectedProveedorId: null,
    selectedProveedorName: '',
    esServicio: false,
    observaciones: '',
    notas: '',
    dercInfo: null,
  });

  const [productos, setProductos] = useState<ArticuloMateriaPrima[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Load initial data when provided (edit mode)
  useEffect(() => {
    if (initialData) {
      setGuiaId(initialData.id);
      setFormData(initialData.formData);
      setProductos(initialData.productos);
    }
  }, [initialData]);

  // Load default product based on DERC species
  const loadDefaultProduct = useCallback(async () => {
    console.log('loadDefaultProduct called with:', {
      productType,
      dercInfo: formData.dercInfo,
      idEspecie: formData.dercInfo?.idEspecie,
      isEntero: formData.dercInfo?.isEntero
    });

    // Load default product for Type A OR Type B "entero"
    // Type B "entero" should behave exactly like Type A
    const isTypeA = productType === 'A';
    const isTypeBEntero = productType === 'B' && formData.dercInfo?.isEntero === true;
    const shouldLoadDefaultProduct = isTypeA || isTypeBEntero;

    if (!shouldLoadDefaultProduct) {
      console.log('Skipping default product load - not product type A or type B entero, current type:', productType, 'isEntero:', formData.dercInfo?.isEntero);
      return;
    }

    if (!formData.dercInfo?.idEspecie) {
      console.log('Skipping default product load - no idEspecie available, formData.dercInfo:', formData.dercInfo);
      return;
    }

    try {
      const idEspecie = formData.dercInfo.idEspecie;
      console.log(`Loading default product for species ID: ${idEspecie}`);

      const defaultArticulo = await ArticuloService.getArticuloByEspecie(idEspecie);
      console.log('API response from getArticuloByEspecie:', defaultArticulo);

      if (defaultArticulo) {
        const coeficiente = typeof defaultArticulo.coeficiente === 'number' ? defaultArticulo.coeficiente : 0;
        const pesoEmpaqueInterior = (defaultArticulo as any).pesoEmpaqueInterior || coeficiente;
        const defaultProduct: ArticuloMateriaPrima = {
          id: Date.now(),
          idArticulo: defaultArticulo.idArticulo,
          codigoArticulo: defaultArticulo.codigoArticulo,
          descripcion: defaultArticulo.descripcion,
          especieNombreComun: defaultArticulo.especieNombreComun || 'N/A',
          coeficiente,
          pesoEmpaqueInterior,
          cantidad: 0,
          total: 0,
          duenio: '',
          encargado: '',
          presentacion: formData.dercInfo?.especieDescripcion || defaultArticulo.especieNombreComun || 'Sin Presentación'
        };

        // Only set products if there are none - don't overwrite existing products
        setProductos(prev => {
          if (prev.length === 0) {
            console.log('Default product loaded successfully:', defaultProduct);
            return [defaultProduct];
          }
          console.log('Products already exist, not overwriting');
          return prev;
        });
      } else {
        console.log(`No default product found for species ID: ${idEspecie} - API returned null/undefined`);
      }
    } catch (error) {
      console.error(`Error loading default product for species ${formData.dercInfo?.idEspecie}:`, error);
    }
  }, [productType, formData.dercInfo?.idEspecie, formData.dercInfo?.isEntero, formData.dercInfo?.especieDescripcion]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        selectedDercId: null,
        selectedDercNumber: '',
        selectedProveedorId: null,
        selectedProveedorName: '',
        esServicio: false,
        observaciones: '',
        notas: '',
        dercInfo: null,
      });
      setProductos([]);
      setErrors({});
      setIsDirty(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Load default product when DERC species is available for type A or type B "entero"
  // BUT only if we don't have initial data (not in edit mode)
  useEffect(() => {
    // Type B "entero" should behave exactly like Type A
    const isTypeA = productType === 'A';
    const isTypeBEntero = productType === 'B' && formData.dercInfo?.isEntero === true;
    const shouldLoadForProductType = isTypeA || isTypeBEntero;

    console.log('useEffect for loadDefaultProduct triggered with:', {
      isOpen,
      productType,
      idEspecie: formData.dercInfo?.idEspecie,
      isEntero: formData.dercInfo?.isEntero,
      hasInitialData: !!initialData,
      productosLength: productos.length,
      shouldLoadForProductType,
      shouldLoad: isOpen && shouldLoadForProductType && formData.dercInfo?.idEspecie && !initialData && productos.length === 0
    });

    // Only load default product if:
    // 1. Form is open
    // 2. Product type is A OR product type is B "entero"
    // 3. We have species info
    // 4. We DON'T have initial data (not editing)
    // 5. We don't already have products loaded
    if (isOpen && shouldLoadForProductType && formData.dercInfo?.idEspecie && !initialData && productos.length === 0) {
      console.log('Calling loadDefaultProduct...');
      loadDefaultProduct();
    }
  }, [isOpen, productType, formData.dercInfo?.idEspecie, formData.dercInfo?.isEntero, loadDefaultProduct, initialData, productos.length]);

  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    // Validate DERC selection
    if (!formData.selectedDercId) {
      newErrors.selectedDercId = 'Debe seleccionar un DERC de referencia';
    }

    // Validate Proveedor selection
    if (!formData.selectedProveedorId) {
      newErrors.selectedProveedorId = 'Debe seleccionar un proveedor';
    }

    // Validate products
    const productosConCantidad = productos.filter(p => p.cantidad > 0);
    if (productos.length === 0) {
      newErrors.productos = 'Debe agregar al menos un producto';
    } else if (productosConCantidad.length === 0) {
      newErrors.productos = 'Al menos un producto debe tener cantidad mayor a 0';
    } else {
      const sinDuenio = productosConCantidad.some(p => !p.duenio || p.duenio === '');
      const sinEncargado = productosConCantidad.some(p => !p.encargado || p.encargado === '');
      if (sinDuenio || sinEncargado) {
        newErrors.productos = 'Todos los productos deben tener Dueño y Encargado asignados';
      }
    }

    return newErrors;
  }, [formData, productos]);

  const isValid = useCallback(() => {
    const formErrors = validateForm();
    return Object.keys(formErrors).length === 0;
  }, [validateForm]);

  // Validate on form data changes to show errors immediately
  useEffect(() => {
    if (isDirty) {
      const formErrors = validateForm();
      setErrors(formErrors);
    }
  }, [formData, productos, isDirty, validateForm]);

  const handleFieldChange = useCallback((field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);

    // Clear specific field error when user starts typing
    setErrors(prev => {
      const newErrors = { ...prev };

      // Clear field-specific errors
      if (field === 'selectedDercId' && value) {
        delete newErrors.selectedDercId;
      }
      if (field === 'selectedProveedorId' && value) {
        delete newErrors.selectedProveedorId;
      }

      return newErrors;
    });
  }, []);

  const handleDercChange = useCallback(async (dercId: number, numeroDocumento: string, dercData?: DERCResponse) => {
    console.log('DERC Change - dercId:', dercId, 'numeroDocumento:', numeroDocumento);
    console.log('DERC Change - dercData:', dercData);

    handleFieldChange('selectedDercId', dercId);
    handleFieldChange('selectedDercNumber', numeroDocumento);

    // Clear products when DERC changes (they will be reloaded based on new species)
    setProductos([]);

    // Si se proporciona dercData, extraer la información del DERC
    if (dercData) {
      const dercInfo: DERCInfo = {
        numeroDocumento: dercData.numeroDocumento || numeroDocumento,
        fechaExtraccion: dercData.fechaExtraccion || '',
        asociacionDescripcion: dercData.asociacionDescripcion || '',
        numeroMallas: dercData.numeroMallas || 0,
        numeroManojos: dercData.numeroManojos || 0,
        placaVehiculo: dercData.placaVehiculo || '',
        idEspecie: dercData.idEspecie || 0,
        especieDescripcion: dercData.especieDescripcion || '',
        isEntero: dercData.isEntero || false,
      };
      console.log('DERC Info created:', dercInfo);
      handleFieldChange('dercInfo', dercInfo);
    } else {
      console.log('No dercData provided');
    }
  }, [handleFieldChange]);

  const handleProveedorChange = useCallback((proveedorId: number, razonSocial: string) => {
    handleFieldChange('selectedProveedorId', proveedorId);
    handleFieldChange('selectedProveedorName', razonSocial);
  }, [handleFieldChange]);

  const handleSwitchChange = useCallback((field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(`Switch change - ${field}:`, event.target.checked);
    handleFieldChange(field, event.target.checked);
  }, [handleFieldChange]);

  const handleInputChange = useCallback((field: keyof FormData) => (value: string) => {
    handleFieldChange(field, value);
  }, [handleFieldChange]);

  const handleCantidadChange = useCallback((id: number, cantidad: number) => {
    setProductos(prev =>
      prev.map(producto =>
        producto.id === id
          ? {
              ...producto,
              cantidad,
              total: producto.pesoEmpaqueInterior * cantidad
            }
          : producto
      )
    );
    setIsDirty(true);

    // Clear productos error when user changes quantity
    if (errors.productos) {
      setErrors(prev => ({
        ...prev,
        productos: undefined
      }));
    }
  }, [errors.productos]);

  const handleDuenioChange = useCallback((id: number, duenio: number) => {
    setProductos(prev =>
      prev.map(producto =>
        producto.id === id
          ? { ...producto, duenio }
          : producto
      )
    );
    setIsDirty(true);
    if (errors.productos) {
      setErrors(prev => ({ ...prev, productos: undefined }));
    }
  }, [errors.productos]);

  const handleEncargadoChange = useCallback((id: number, encargado: number) => {
    setProductos(prev =>
      prev.map(producto =>
        producto.id === id
          ? { ...producto, encargado }
          : producto
      )
    );
    setIsDirty(true);
    if (errors.productos) {
      setErrors(prev => ({ ...prev, productos: undefined }));
    }
  }, [errors.productos]);

  const handleRemoveProducto = useCallback((id: number) => {
    setProductos(prev => prev.filter(producto => producto.id !== id));
    setIsDirty(true);
  }, []);

  const addProducto = useCallback((producto: ArticuloMateriaPrima) => {
    setProductos(prev => [...prev, producto]);
    setIsDirty(true);

    // Clear productos error when user adds a product
    if (errors.productos) {
      setErrors(prev => ({
        ...prev,
        productos: undefined
      }));
    }
  }, [errors.productos]);

  const addMultipleProductos = useCallback((productosToAdd: ArticuloMateriaPrima[]) => {
    setProductos(prev => [...prev, ...productosToAdd]);
    setIsDirty(true);

    // Clear productos error when user adds products
    if (errors.productos) {
      setErrors(prev => ({
        ...prev,
        productos: undefined
      }));
    }
  }, [errors.productos]);

  const handleAddProducto = useCallback(async () => {
    // Type B "entero" should behave exactly like Type A
    const isTypeA = productType === 'A';
    const isTypeBEntero = productType === 'B' && formData.dercInfo?.isEntero === true;
    const shouldUseDefaultProduct = isTypeA || isTypeBEntero;

    // Si es producto tipo A o tipo B "entero" y tenemos idEspecie, cargar el artículo por defecto
    if (shouldUseDefaultProduct && formData.dercInfo?.idEspecie) {
      try {
        const idEspecie = formData.dercInfo.idEspecie;
        console.log(`Adding default product for species ID: ${idEspecie} (Type: ${productType}, isEntero: ${formData.dercInfo?.isEntero})`);

        const defaultArticulo = await ArticuloService.getArticuloByEspecie(idEspecie);

        if (defaultArticulo) {
          const coeficiente = typeof defaultArticulo.coeficiente === 'number' ? defaultArticulo.coeficiente : 0;
          const pesoEmpaqueInterior = (defaultArticulo as any).pesoEmpaqueInterior || coeficiente;
          const newProduct: ArticuloMateriaPrima = {
            id: Date.now(),
            idArticulo: defaultArticulo.idArticulo,
            codigoArticulo: defaultArticulo.codigoArticulo,
            descripcion: defaultArticulo.descripcion,
            especieNombreComun: defaultArticulo.especieNombreComun || 'N/A',
            coeficiente,
            pesoEmpaqueInterior,
            cantidad: 0,
            total: 0,
            duenio: '',
            encargado: '',
            presentacion: formData.dercInfo?.especieDescripcion || defaultArticulo.especieNombreComun || 'Sin Presentación'
          };

          addProducto(newProduct);
          console.log('Default product added successfully:', newProduct);
          return;
        }
      } catch (error) {
        console.error(`Error adding default product for species ${formData.dercInfo?.idEspecie}:`, error);
      }
    }

    // Si es producto tipo B (NO entero) y tenemos idEspecie, abrir modal de presentaciones
    if (productType === 'B' && !formData.dercInfo?.isEntero && formData.dercInfo?.idEspecie) {
      // Retornar una función que abrirá el modal
      return 'openPresentacionModal';
    }

    // Si no es tipo A o B, o no hay especie, agregar producto vacío
    const newProduct: ArticuloMateriaPrima = {
      id: Date.now(),
      idArticulo: 0,
      codigoArticulo: '',
      descripcion: '',
      especieNombreComun: '',
      coeficiente: 0,
      pesoEmpaqueInterior: 0,
      cantidad: 0,
      total: 0,
      duenio: '',
      encargado: ''
    };

    addProducto(newProduct);
  }, [productType, formData.dercInfo?.idEspecie, formData.dercInfo?.isEntero, formData.dercInfo?.especieDescripcion, addProducto]);

  const handleSubmit = useCallback(async () => {
    // Always validate and show errors on submit attempt
    const formErrors = validateForm();
    setErrors(formErrors);
    setIsDirty(true); // Mark as dirty to ensure errors are shown

    if (Object.keys(formErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const productosConCantidad = productos.filter(p => p.cantidad > 0);
      const requestBody = {
        id: guiaId,
        idDocumento: formData.selectedDercId,
        idProveedor: formData.selectedProveedorId,
        esServicio: formData.esServicio,
        observaciones: formData.observaciones,
        notas: formData.notas,
        detalles: productosConCantidad.map(producto => ({
          id: (producto as any).detalleId || 0,
          idArticulo: producto.idArticulo,
          cantidad: producto.cantidad,
          coeficiente: producto.coeficiente || 0,
          descripcion: producto.descripcion,
          idDuenio: typeof producto.duenio === 'number' ? producto.duenio : null,
          idEncargado: typeof producto.encargado === 'number' ? producto.encargado : null
        }))
      };

      await onSubmit(requestBody);
    } finally {
      setIsSubmitting(false);
    }
  }, [guiaId, formData, productos, validateForm, onSubmit]);

  return {
    formData,
    productos,
    errors,
    isValid: isValid(),
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleDercChange,
    handleProveedorChange,
    handleSwitchChange,
    handleInputChange,
    handleCantidadChange,
    handleDuenioChange,
    handleEncargadoChange,
    handleRemoveProducto,
    addProducto,
    addMultipleProductos,
    handleAddProducto,
    handleSubmit,
  };
};